import type {ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import {BAND_TONE} from '@/lib/bands'
import {formatDate, formatRatePct} from '@/lib/risk'
import type {Expediente} from '@/hooks/sgsst/evaluacion/use-sgsst'

function SealStamp({year}: {year: number}) {
  const {t} = useTranslation()
  return (
    <span
      data-testid="expediente-stamp"
      className="inline-flex items-center gap-2 rounded-md border border-primary/30 bg-primary/5 px-2.5 py-1 font-mono text-[11px] font-semibold tracking-widest text-primary uppercase">
      <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-primary" />
      {t('expediente.stamp')} · {year}
    </span>
  )
}

export function ExpedienteChrome({
  exp,
  backTo,
  backLabel,
  children,
}: {
  exp: Expediente
  backTo: string
  backLabel: string
  children: ReactNode
}) {
  const {t} = useTranslation()
  const year = exp.period_year
  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <Link to={backTo} data-testid="expediente-back" className="inline-block text-sm text-primary hover:underline">
          {backLabel}
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-xl text-foreground">{t('expediente.title', {year})}</h1>
          <SealStamp year={year} />
          <Tag tone={BAND_TONE[exp.band]}>{t(`sgsst.bands.${exp.band}.label`)}</Tag>
          <span className="font-mono text-sm text-primary tabular-nums">{formatRatePct(exp.total_pct)}</span>
        </div>
        <p className="text-xs text-muted-foreground">
          {exp.closed_by
            ? t('expediente.sealedByLine', {
                date: formatDate(exp.closed_at),
                name: `${exp.closed_by.name} ${exp.closed_by.last_name}`,
              })
            : t('expediente.sealedOnLine', {date: formatDate(exp.closed_at)})}
        </p>
      </div>
      {children}
    </div>
  )
}
